import { v4 as uuidv4 } from 'uuid';
import { MAX_FILE_UPLOAD_SIZE, toastVariant } from '../../constants/misc';
import { showToast } from '../../graphql/cache/modal';
import { useApolloClient, useMutation } from '@apollo/client';
import { loader } from 'graphql.macro';
import { useCurrentUser } from '../../providers/UserProvider';
import { CF_DOMAIN } from '../../constants/aws';
import axios from 'axios';
import { useOnlineStatus } from './offline-misc';
import { addOfflineFile } from './localForageDb';

const uploadUrlQuery = loader('../../graphql/queries/s3.graphql');
const updateFileMutation = loader('./update_file.graphql');
const addFileToFilesTableMutation = loader(
  'src/pages/files/FilesPage.addFile.graphql',
);
export const useFileUpload = () => {
  const client = useApolloClient();
  const { user } = useCurrentUser();
  const online = useOnlineStatus();
  const [updateFileMu] = useMutation(updateFileMutation);
  const [addFileToFiles] = useMutation(addFileToFilesTableMutation);

  const addFileToS3 = async ({
    file,
    onSubmit = () => {},
    setLoading = () => {},
  }) => {
    const fileUUID = uuidv4();

    if (!online) {
      await addOfflineFile(fileUUID, file);
      const offline = { ...file, fileUUID };
      onSubmit(offline);
      return offline;
    }

    setLoading(true);
    try {
      return await addOnlineFile({ file, onSubmit, fileUUID });
    } finally {
      setLoading(false);
    }
  };
  const updateFile = async ({
    file,
    requirementId,
    expires,
    onComplete = () => {},
    hidden,
    userId,
    isCertification,
    description,
  }) => {
    updateFileMu({
      variables: {
        ...file,
        id: file.id,
        requirementId,
        expires,
        hidden,
        userId,
        isCertification,
        description,
      },
    }).then(() => onComplete());
  };

  const addOnlineFile = async ({ file, onSubmit, fileUUID }) => {
    const name = file?.name || `${fileUUID}.webm`;
    const ext = name.split('.').pop().toLowerCase();
    const size = file?.size;

    if (size > MAX_FILE_UPLOAD_SIZE) {
      showToast({
        variant: toastVariant.danger,
        title: 'Error',
        message: 'File size too large',
      });
      throw new Error('File too large');
    }

    const key = `assets/${user.company.id}/${fileUUID}.${ext}`;

    const { data } = await client.query({
      query: uploadUrlQuery,
      variables: { fileName: key, fileType: ext },
      fetchPolicy: 'no-cache',
    });

    const signedUrl = data.simpleStorageUploadUrl;

    await axios.put(signedUrl, file, {
      headers: {
        'Content-Type': ext === 'pdf' ? 'application/pdf' : ext,
      },
    });

    const uploaded = {
      url: `${CF_DOMAIN(user)}${fileUUID}.${ext}`,
      name,
      extension: ext,
      size,
    };

    onSubmit(uploaded);
    return uploaded;
  };

  const addFile = ({ file, onComplete = () => {} }) => {
    addOnlineFile({
      file,
      onSubmit: ({ url, name, extension, size }) => {
        addFileToFiles({
          variables: { ...file, url, name, extension, size },
        }).then(() => onComplete());
      },
    });
  };

  const saveFileToFiles = async ({ file }) => {
    return addFileToFiles({
      variables: { ...file },
    });
  };

  return { addOnlineFile, addFileToS3, updateFile, addFile, saveFileToFiles };
};
